/* «Описание товара» (§7.2): лоток фото товара в общей рамке (.bcu.multi.tt-tray), до стольких фото, сколько полей photo… публикует форма
   (photo, photo_2…photo_5): пусто — «Загрузите фото товара · По фото найдём материал, цвет и детали», с фото — «Фото товара · 1 из 5»,
   квадраты с «×», «＋» в конце; полный лоток — «＋» неактивен, дрожь и «Можно до 5 фото». */
import { useEffect, useId, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';
import type { ToolField } from '../../studio/circle/tool-form';
import { StudioIcon } from '../../studio/StudioIcon';
import { useStudioPhotoPreviews } from '../../studio/use-studio-photo-previews';
import { shake } from '../../../ui/motion';
import { limitsNote } from './text-model';
import { FrameEmpty, RightsCap } from './TextFrameParts';
import { useConsentUpload } from './use-consent-upload';

export function TextProductTray({ state, slots }: { state: CircleFormState; slots: ToolField[] }) {
  const inputId = useId();
  const frame = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [local, setLocal] = useState<Record<string, string>>({});
  const consent = useConsentUpload(state);
  const previews = useStudioPhotoPreviews(state, slots);
  const localRef = useRef(local);
  useEffect(() => {
    localRef.current = local;
  }, [local]);
  useEffect(() => () => Object.values(localRef.current).forEach(url => URL.revokeObjectURL(url)), []);
  const taken = slots.filter(slot => state.inputs[slot.key] || state.uploadIds[slot.key]);
  const free = slots.filter(slot => !state.inputs[slot.key] && !state.uploadIds[slot.key]);
  const accept = slots[0]?.upload?.accept ?? [];
  const maximum = Math.min(...slots.map(slot => slot.upload?.max_bytes ?? 10485760));
  const fail = (text: string) => {
    setError(text);
    shake(frame.current);
  };
  function select(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    setError('');
    if (!free.length) return fail(uiFormat('Можно до {0} фото', slots.length));
    const ok = files.filter(file => accept.includes(file.type) && file.size <= maximum);
    if (!ok.length) return fail(uiFormat('Нужно фото {0}', limitsNote(slots.slice(0, 1))));
    if (files.length > free.length) setError(uiFormat('Можно до {0} фото', slots.length));
    const chosen = ok.slice(0, free.length);
    const run = async (current: CircleFormState) => {
      const targets = slots.filter(slot => !current.inputs[slot.key] && !current.uploadIds[slot.key]);
      for (const [index, file] of chosen.entries()) {
        const slot = targets[index];
        if (!slot) break;
        const url = URL.createObjectURL(file);
        setLocal(value => ({ ...value, [slot.key]: url }));
        if (!(await current.upload(slot.key, file))) break;
      }
    };
    consent.take(chosen[0], (_file, current) => void run(current));
  }
  const remove = (key: string) => {
    state.clearUpload(key);
    setLocal(value => {
      if (value[key]) URL.revokeObjectURL(value[key]);
      const next = { ...value };
      delete next[key];
      return next;
    });
  };
  const disabled = state.locked;
  return (
    <div className="pf-up tt-up">
      <div ref={frame} className={`bcu multi tt-tray${taken.length ? ' has fit' : ''}`} id="ttSeoUp">
        <input
          id={inputId}
          type="file"
          hidden
          multiple
          accept={accept.join(',')}
          disabled={disabled || !free.length}
          aria-label={uiText('Фото товара')}
          onChange={event => {
            select(event.target.files);
            event.target.value = '';
          }}
        />
        <div className="bcu-st">
          {!taken.length ? (
            <FrameEmpty
              inputId={inputId}
              icon="box"
              title="Загрузите фото товара"
              sub="По фото найдём материал, цвет и детали"
              note={error || limitsNote(slots.slice(0, 1))}
              bad={!!error}
              disabled={disabled}
            />
          ) : (
            <div className="bcu-m">
              <div className="bcu-mh">
                <b>{uiText('Фото товара')}</b>
                <span className="bcu-mc">
                  <b className="mono">{taken.length}</b> {uiText('из')} <span className="mono">{slots.length}</span>
                </span>
              </div>
              <div className="bcu-tr nosb">
                {taken.map((slot, index) => {
                  const url = local[slot.key] || previews.urls[slot.key];
                  return (
                    <span className="bcu-sq" key={slot.key}>
                      {url ? (
                        <img src={url} alt={uiFormat('Фото товара {0}', index + 1)} />
                      ) : (
                        <span className="bcu-sqd">
                          <StudioIcon kind="image" />
                          <small>{uiText(state.inputs[slot.key] ? 'Загружено' : 'Загружаем…')}</small>
                        </span>
                      )}
                      <button className="x" type="button" disabled={disabled} aria-label={uiFormat('Убрать {0}', index + 1)} onClick={() => remove(slot.key)}>
                        <StudioIcon kind="close" />
                      </button>
                    </span>
                  );
                })}
                {free.length ? (
                  <label className="bcu-sq add" htmlFor={inputId} role="button" tabIndex={0} aria-label={uiText('Добавить')}>
                    <StudioIcon kind="plus" />
                  </label>
                ) : (
                  <button
                    className="bcu-sq add off"
                    type="button"
                    aria-disabled="true"
                    aria-label={uiFormat('Можно до {0} фото', slots.length)}
                    onClick={() => fail(uiFormat('Можно до {0} фото', slots.length))}
                  >
                    <StudioIcon kind="plus" />
                  </button>
                )}
              </div>
              <small className={`bcu-n${error ? ' bad' : ''}`} role={error ? 'alert' : undefined}>
                {uiText(error || 'По фото найдём материал, цвет и детали')}
              </small>
            </div>
          )}
        </div>
      </div>
      {slots
        .filter(slot => state.uploadIds[slot.key] && !state.inputs[slot.key])
        .slice(0, 1)
        .map(slot => (
          <button key={slot.key} type="button" className="lnk" disabled={state.busy} onClick={() => void state.upload(slot.key, null)}>
            {uiText('Продолжить проверку')}
          </button>
        ))}
      <RightsCap />
      {consent.sheet}
    </div>
  );
}
